import React, { FC, useCallback, useContext, useEffect, useRef, useState } from "react";

import { CommonColors } from "../../types/colors";
import { TabsContext } from "./tabs-context";
import { TabProps, TabsVariants } from "./types";
import {
  PillTabHeaderItem,
  PillTabsHeader,
  SquaredTabHeaderItem,
  SquaredTabsHeader,
} from "./variants";

export interface TabsHeadersProps {
  tabs: TabProps[];
  variant?: TabsVariants;
  colorScheme?: CommonColors;
}

export const TabsHeaders: FC<TabsHeadersProps> = ({
  tabs,
  variant = TabsVariants.Pill,
  colorScheme = CommonColors.Light,
}) => {
  const { activeTab } = useContext(TabsContext);
  const selectedTabIndex = tabs.findIndex(({ id }) => id === activeTab);

  const rootRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<Array<HTMLDivElement | null>>([]);
  const [tabIndicatorWidth, setTabIndicatorWidth] = useState(0);
  const [tabIndicatorLeft, setTabIndicatorLeft] = useState(0);

  const onMoveSelectedTabIndicator = useCallback((selectedTabIndex: number) => {
    const selectedTabRect = tabsRef.current[selectedTabIndex]?.getBoundingClientRect();
    const rootRect = rootRef.current?.getBoundingClientRect();
    if (selectedTabRect) {
      setTabIndicatorWidth(selectedTabRect.width);
      setTabIndicatorLeft(
        selectedTabRect.left - (rootRect?.left ?? 0) + (rootRef.current?.scrollLeft ?? 0)
      );
    }

    return requestAnimationFrame(() => {
      onMoveSelectedTabIndicator(selectedTabIndex);
    });
  }, []);

  useEffect(() => {
    const requestId = onMoveSelectedTabIndicator(selectedTabIndex);
    return () => {
      cancelAnimationFrame(requestId);
    };
  }, [selectedTabIndex, onMoveSelectedTabIndicator]);

  const content = tabs.map(({ id, header, subHeader }, i) => {
    switch (variant) {
      case TabsVariants.Squared:
        return (
          <SquaredTabHeaderItem
            ref={(el) => (tabsRef.current[i] = el)}
            key={id}
            id={id}
            color={colorScheme}
            header={header}
            subHeader={subHeader}
          />
        );
      case TabsVariants.Pill:
      default:
        return (
          <PillTabHeaderItem
            color={colorScheme}
            key={id}
            id={id}
            header={header}
            subHeader={subHeader}
          />
        );
    }
  });

  switch (variant) {
    case TabsVariants.Squared:
      return (
        <SquaredTabsHeader
          {...{ tabIndicatorWidth, tabIndicatorLeft }}
          ref={rootRef}
          color={colorScheme}
        >
          {content}
        </SquaredTabsHeader>
      );
    case TabsVariants.Pill:
    default:
      return <PillTabsHeader>{content}</PillTabsHeader>;
  }
};
